import { useTranslation } from 'react-i18next'
import { TimelineItem } from '../state/types'
import { stepLabel } from '../util/activity'
import { Markdown } from './Markdown'

// Glyph shown inside a completed/failed tool node (running uses a CSS spinner).
const TOOL_GLYPH: Record<'running' | 'ok' | 'failed', string> = {
  running: '',
  ok: '✓',
  failed: '✕',
}

// BEM modifier for a tool node's tone, keyed by the same status union as TOOL_GLYPH above.
const NODE_TONE: Record<'running' | 'ok' | 'failed', string> = { running: 'run', ok: 'ok', failed: 'fail' }

interface TimelineProps {
  items: TimelineItem[]
  /** Supplied by ActivityLog so expanded steps get a per-kind icon; omitted elsewhere. */
  renderStepIcon?: (item: TimelineItem) => string
  /** Supplied by ActivityLog so expanded steps show their clock time. */
  renderStepTime?: (at: number) => string
}

export function Timeline({ items, renderStepIcon, renderStepTime }: Readonly<TimelineProps>) {
  const { t } = useTranslation()
  return (
    <ul className="copilot-rail" aria-live="polite">
      {items.map((item) => {
        if (item.kind === 'status') {
          return (
            <li key={item.id} className="copilot-rail__row">
              <span className="copilot-rail__node copilot-rail__node--status" aria-hidden="true" />
              <Markdown source={item.text} className="copilot-rail__text" />
            </li>
          )
        }
        if (item.kind === 'tool') {
          return (
            <li key={item.id} className={`copilot-rail__row${item.status === 'running' ? ' is-running' : ''}`}>
              {renderStepIcon ? (
                <span className="copilot-rail__node copilot-rail__node--step" aria-hidden="true">
                  <i className={`fa ${renderStepIcon(item)}`} />
                </span>
              ) : (
                <span
                  className={`copilot-rail__node copilot-rail__node--${NODE_TONE[item.status]}`}
                  aria-hidden="true"
                >
                  {TOOL_GLYPH[item.status]}
                </span>
              )}
              <span className="copilot-rail__op">{stepLabel(t, item)}</span>
              {renderStepTime && item.at > 0 && (
                <span className="copilot-rail__at">{renderStepTime(item.at)}</span>
              )}
            </li>
          )
        }
        if (item.kind === 'error') {
          return (
            <li key={item.id} className="copilot-rail__row">
              <span
                className={`copilot-rail__node ${item.cancelled ? 'copilot-rail__node--status' : 'copilot-rail__node--fail'}`}
                aria-hidden="true"
              >
                {item.cancelled ? '' : '✕'}
              </span>
              <span className={`copilot-rail__text ${item.cancelled ? 'copilot-rail__text--cancelled' : 'copilot-rail__text--error'}`}>
                {item.message}
              </span>
            </li>
          )
        }
        // A `user` item never reaches Timeline — threadSegments (util/activity.ts) always pulls
        // operator turns out into their own `turn` segment before a `work` run gets here. This
        // branch exists only so the if-chain stays exhaustive for the type checker.
        return null
      })}
    </ul>
  )
}
